// Atlas Shared Constants — Status labels and display values
// Used by the dashboard and extension for consistent status display.

import type {
  OpenStatusLabel,
  ReplyStatus,
  EngagementTrend,
  ConversationState,
  CampaignStatus,
  CampaignPacing,
} from "../types/index";

// ─── Open status display configuration ───
export const OPEN_STATUS_CONFIG: Record<
  OpenStatusLabel,
  { label: string; color: string; icon: string }
> = {
  opened: { label: "Opened", color: "green", icon: "eye" },
  likely_opened: { label: "Likely opened", color: "amber", icon: "eye" },
  not_detected: { label: "Not detected", color: "gray", icon: "circle-dashed" },
};

// ─── Reply status display configuration ───
export const REPLY_STATUS_CONFIG: Record<
  ReplyStatus,
  { label: string; color: string; icon: string }
> = {
  replied: { label: "Replied", color: "blue", icon: "message-circle" },
  no_reply: { label: "No reply", color: "gray", icon: "clock" },
  overdue: { label: "Reply overdue", color: "amber", icon: "alert-triangle" },
};

// ─── Engagement trend display configuration ───
export const ENGAGEMENT_TREND_CONFIG: Record<
  EngagementTrend,
  { label: string; color: string; icon: string }
> = {
  warming: { label: "Warming", color: "green", icon: "trending-up" },
  stable: { label: "Stable", color: "blue", icon: "minus" },
  cooling: { label: "Cooling", color: "amber", icon: "trending-down" },
  new: { label: "New", color: "gray", icon: "sparkles" },
  no_recent_activity: { label: "No recent activity", color: "gray", icon: "moon" },
  limited_data: { label: "Limited data", color: "gray", icon: "help-circle" },
};

// ─── Conversation state display configuration ───
export const CONVERSATION_STATE_CONFIG: Record<
  ConversationState,
  { label: string; color: string; icon: string }
> = {
  new: { label: "New", color: "gray", icon: "circle" },
  waiting_for_reply: { label: "Waiting for reply", color: "gray", icon: "clock" },
  reply_received: { label: "Reply received", color: "blue", icon: "message-circle" },
  no_reply_overdue: { label: "No reply (overdue)", color: "amber", icon: "alert-triangle" },
  ended: { label: "Ended", color: "gray", icon: "check" },
};

// ─── Campaign status display configuration ───
export const CAMPAIGN_STATUS_CONFIG: Record<
  CampaignStatus,
  { label: string; color: string; icon: string }
> = {
  draft: { label: "Draft", color: "gray", icon: "file-edit" },
  scheduled: { label: "Scheduled", color: "blue", icon: "calendar" },
  sending: { label: "Sending", color: "blue", icon: "send" },
  paused: { label: "Paused", color: "gray", icon: "pause" },
  completed: { label: "Completed", color: "green", icon: "check-circle" },
  cancelled: { label: "Cancelled", color: "gray", icon: "x-circle" },
};

// ─── Campaign pacing display configuration ───
export const CAMPAIGN_PACING_CONFIG: Record<
  CampaignPacing,
  { label: string; description: string }
> = {
  fast: { label: "Fast", description: "1 email per 10 seconds (~360/hour)" },
  normal: { label: "Normal", description: "1 email per 30 seconds (~120/hour)" },
  slow: { label: "Slow", description: "1 email per 2 minutes (~30/hour)" },
};

// ─── System health display configuration ───
export const HEALTH_STATUS_CONFIG = {
  healthy: { label: "Healthy", color: "green", icon: "check-circle" },
  warning: { label: "Warning", color: "amber", icon: "alert-triangle" },
  error: { label: "Error", color: "red", icon: "x-circle" },
} as const;

// ─── Gmail connection display configuration ───
export const GMAIL_STATUS_CONFIG = {
  connected: { label: "Connected", color: "green", icon: "check-circle" },
  expired: { label: "Reconnect needed", color: "amber", icon: "alert-triangle" },
  disconnected: { label: "Not connected", color: "gray", icon: "x-circle" },
  error: { label: "Error", color: "red", icon: "x-circle" },
} as const;
